import React, { useEffect, useMemo, useRef, useState } from "react"
import { createFilterOptions, Box, IconButton, List, ListItem, Paper, Popper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, useAutocomplete, useTheme, Typography, Popover, Button, SvgIcon, ClickAwayListener } from "@mui/material";
import { ArrowBack, ArrowForward, AssignmentInd, CallSplit, CheckCircle, Clear, NoteAdd, Save, SaveAlt, ThumbDown, Update, Warning, Error} from '@mui/icons-material';
import { useParams } from "react-router-dom";
import { t } from "i18next";
import { ICurrencyAutocompleteGuiModel } from "../../../model/ICurrencyGuiModel";
import { IAmountGuiModel } from "../../../model/ISourceDocumentGuiModel";
import CurrencyBusinessLogic from "../../../businessLogic/CurrencyBusinessLogic";
import { NumericFormatDecimalLocale, NumericFormatWhole } from "../../../common/CustomFunctions";
import { ReactComponent as currency } from "../../../common/icons/currency.svg"
import { DesktopDatePicker } from "@mui/x-date-pickers";
import AutocompleteCurrencies from "./AutocompleteCurrencies";
import CurrencyService from "../../../service/CurrencyService";
import { ICurrencyRateModel } from "../../../service/model/ICurrencyModel";
import { toast } from "react-toastify";
interface AmountProps {
    amountObj: IAmountGuiModel
    handleUpdateAmount: (amount: number | undefined, error: boolean) => void,
    handleUpdateCurrencyAmount: (amount: number | undefined, error: boolean) => void,
    handleUpdateCurrencyDate: (date: Date | null, error: boolean) => void,
    handleUpdateCurrencyRate: (rate: number | undefined, error: boolean) => void,
    handleUpdateCurrencyId: (id: number | undefined, error: boolean) => void,
    handleUpdateCurrencyAmountDeleteAll: () => void,
    handleUpdateAmountError: (status: boolean) => void,
    handleInputOnEnter: () => void,
    focusOnCidInput: () => void,
}

const DialogVoucherRegistrationAmountInput = (props: AmountProps) => {

    const { organizationId } = useParams();


    const theme = useTheme();

    const {
        amountObj,
        handleUpdateAmount,
        handleUpdateCurrencyAmount,
        handleUpdateCurrencyDate,
        handleUpdateCurrencyRate,
        handleUpdateCurrencyId,
        handleUpdateCurrencyAmountDeleteAll,
        handleUpdateAmountError,
        handleInputOnEnter,
        focusOnCidInput,
    } = props;

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const handleAnchorEl = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLDivElement> | React.KeyboardEvent<HTMLButtonElement>) => {
        setAnchorEl(anchorEl ? null : event.currentTarget);
    }
    const handleDeleteCurrency = () => {
        setAnchorEl(null);
        handleUpdateCurrencyAmountDeleteAll();
    }

    const handleEditAndClose = (focusCid?: boolean) => {
        setAnchorEl(null);
        if (focusCid) {
            focusOnCidInput();
        }
    };

    const isUserDefinedAmount = !!amountObj?.amount;

    useEffect(() => {
        let isMounted = true;

        const fetchCurrencyRate = async () => {
            if (organizationId && amountObj.currency?.currencyVismaId && amountObj.currency.currencyDate) {
                try {
                    const service = new CurrencyBusinessLogic();

                    const result = await service.getCurrencyRate(
                        organizationId,
                        amountObj.currency.currencyVismaId,
                        amountObj.currency.currencyDate
                    );

                    if (isMounted) {
                        if (result && result.value && result.value.rate > 0) {
                            if (!isUserDefinedAmount) {
                                handleUpdateCurrencyRate(result.value.rate, false);
                            }
                        } else {
                            handleUpdateCurrencyRate(undefined, true);
                        }
                    }
                } catch (error) {
                    if (isMounted) {
                        handleUpdateCurrencyRate(undefined, true);
                        toast(t("voucherRegistration.errorFetchingCurrencyRate"));
                    }
                }
            }
        };

        fetchCurrencyRate();

        return () => {
            isMounted = false;
        };
    }, [organizationId, amountObj.currency?.currencyVismaId, amountObj.currency?.currencyDate, isUserDefinedAmount]);

    useEffect(() => {
        //Check for errors
        if (amountObj.currency && (amountObj.currency.currencyAmount ||
            amountObj.currency.currencyDate ||
            amountObj.currency.currencyRate ||
            amountObj.currency.currencyVismaId)) {
            if (amountObj.currency.currencyAmount &&
                amountObj.currency.currencyDate &&
                amountObj.currency.currencyRate &&
                amountObj.currency.currencyVismaId) {
                handleUpdateAmountError(false);
            } else {
                handleUpdateAmountError(true);
            }
        } else {
            handleUpdateAmountError(false);
        }
    }, [amountObj.currency, handleUpdateAmountError])

    return <>
        <Stack direction="row" bgcolor={theme.palette.background.paper}>
            <TextField
                disabled={amountObj.currency ? true : false}
                autoComplete="off"
                size="small"
                variant="filled"
                error={amountObj.error ? true : false}
                value={amountObj.amount ?? ""}
                onBlur={(e) => {
                    handleUpdateAmount(parseFloat(e.target.value.replace(",", ".")), false)
                }}
                onKeyUp={(e) => {
                    if (e.key === "Enter") {
                        handleInputOnEnter();
                    }
                }}
                onKeyDown={(e) => {
                    if (e.keyCode === 40) {
                        handleAnchorEl(e);
                    }
                } }
                InputLabelProps={{ shrink: true, }}
                inputProps={{ style: { textAlign: "right" } }}
                fullWidth
                sx={{
                    minWidth: "5.5rem", "& .MuiInputBase-root": { height: "2.5rem" },
                    "& .MuiFilledInput-root": { backgroundColor: theme.palette.background.paper },
                    border: amountObj.error ? 2 : 0,
                    borderColor: "red"
                }}
                InputProps={{ inputComponent: NumericFormatDecimalLocale as any, disableUnderline: true }}
                id="amountId" />
            <IconButton sx={{ margin: 0, padding: 0, }}
                onClick={handleAnchorEl}
                onKeyDown={(e) => {
                    if (e.keyCode === 40) {
                        handleAnchorEl(e);
                    }
                }}> 
                <SvgIcon component={currency} color="primary" sx={{ fontSize: "1.7rem"}} inheritViewBox />
            </IconButton>
        </Stack>
        <Popper sx={{ zIndex: 2000, }} open={Boolean(anchorEl)} anchorEl={anchorEl} placement="bottom">
            <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
                <Box component={Paper} sx={{ border: 1, borderColor: 'divider', padding: theme.spacing(2) }} elevation={10}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ marginBottom: theme.spacing(2) }}>
                        <Typography variant="button">{t("basic.changeCurrency")}</Typography>
                    </Stack>
                    <Stack direction="row" spacing={2}>
                        <DesktopDatePicker
                            autoFocus={true}
                            disableOpenPicker
                            label={t("dateSearch.date")}
                            value={amountObj.currency && amountObj.currency.currencyDate ? new Date(amountObj.currency.currencyDate) : null}
                            onChange={(newValue, context) => handleUpdateCurrencyDate(newValue, false)}
                            slotProps={{
                                textField: {
                                    size: "small",
                                    variant: "filled",
                                    InputProps: { disableUnderline: true },
                                    sx: {
                                        width: "8rem",
                                        "& .MuiInputBase-root": { height: "2.5rem" },
                                        "& .MuiFilledInput-root": {
                                            border: amountObj.currency?.currencyDate ? 0 : 2,
                                            borderColor: "red"
                                        },
                                    },
                                },
                            }} />
                        <AutocompleteCurrencies amountObj={amountObj} handleUpdateCurrencyId={handleUpdateCurrencyId} />
                        <TextField
                            label={t('basic.exchangeRate')}
                            size="small"
                            variant="filled"
                            value={amountObj.currency && amountObj.currency.currencyRate ? amountObj.currency.currencyRate : ""}
                            error={amountObj.currency && amountObj.currency.currencyRate ? false : true}
                            onBlur={(event) => handleUpdateCurrencyRate(parseFloat(event.target.value.replace(",", ".")), false)}
                            sx={{
                                width: "9rem", "& .MuiInputBase-root": { height: "2.5rem" },
                                border: amountObj.currency && amountObj.currency.currencyRate ? 0 : 2,
                                borderColor: "red"
                            }}
                            InputProps={{ inputComponent: NumericFormatDecimalLocale as any, disableUnderline: true }}
                        />
                        <TextField
                            onKeyDown={(e) => {
                                if (e.key === "Tab" && !e.shiftKey) {
                                    e.preventDefault();
                                    handleEditAndClose(true);
                                }
                            }}
                            label={t('search.amount')}
                            size="small"
                            variant="filled"
                            value={amountObj.currency && amountObj.currency.currencyAmount ? amountObj.currency.currencyAmount : ""}
                            error={amountObj.currency && amountObj.currency.currencyAmount ? false : true}
                            onBlur={(event) => handleUpdateCurrencyAmount(parseFloat(event.target.value.replace(",", ".")), false)}
                            sx={{
                                width: "9rem", "& .MuiInputBase-root": { height: "2.5rem" },
                                border: amountObj.currency && amountObj.currency.currencyRate ? 0 : 2,
                                borderColor: "red"
                            }}
                            InputProps={{ inputComponent: NumericFormatDecimalLocale as any, disableUnderline: true }}
                        />
                    </Stack>
                    <Box sx={{ marginRight: 'auto', marginTop: theme.spacing(5) }}>
                        <Button size="small" variant="outlined" onClick={handleDeleteCurrency}>
                            {t("voucherRegistration.removeCurrency")}
                        </Button>
                    </Box>
                </Box>
            </ClickAwayListener>
        </Popper>
    </>
}
export default DialogVoucherRegistrationAmountInput;